/* eslint-disable camelcase */
import React, {ReactElement, useEffect, useState} from 'react';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Tooltip
} from '@material-ui/core';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CustomSelectField from '@components/system/CustomSelectField/CustomSelectField';
import {AppPermissions, CCMPlanTaskArray} from '@shared/constants/Mapping';
import PrimaryButton from '@components/system/ButtonField/PrimaryButton';
import {
  GROUP_TYPE_STYLES,
  STATE_OBJECT_BORDER,
  STATE_OBJECT_OPACITY
} from 'src/style/variable';
import {
  createCCMPlanforPatient,
  getPatientCCMTaskDetails,
  updateCCMPlan
} from '@shared/services/CCMServices';
import {useSelector} from 'react-redux';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import WithPermission from '@components/system/WithPermission/WithPermission';
import {CommunicationService} from 'src/service/CommunicationService';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import {ccmCreatePlanDialogStyles} from './CCMCreatePlanStyle';

interface CCMDialogProps {
  open: boolean;
  patientName: string;
  accountNumber: string;
  onClose(response: 'confirm' | 'cancel'): void;
  fromEdit?: boolean;
  ccmPatientId?: number | undefined;
  clinicId?: number | string;
  isExternalPlanActiveProp?: boolean;
}
interface TaskFields {
  // FIXME:  need to update the itemId param and implement other way
  itemId: number | string | undefined;
  task_name: string;
  task_type: string;
}
interface TaskFieldInputs {
  plan: Array<TaskFields>;
}

const stateWithOpacity = {
  border: `1px solid ${STATE_OBJECT_BORDER}`,
  height: '2.3rem',
  opacity: STATE_OBJECT_OPACITY
};
const stateStyles = {
  border: `1px solid ${GROUP_TYPE_STYLES}`,
  height: '2.3rem'
};

const stateErrorStyles = {
  border: '1.9px solid red',
  height: '2.3rem',
  opacity: 0.5
};
const CCMCreatePlanDialog = (props: CCMDialogProps): ReactElement => {
  const {
    open,
    onClose,
    patientName,
    accountNumber,
    fromEdit = false,
    ccmPatientId,
    clinicId,
    isExternalPlanActiveProp
  } = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [buttonsDisable, setButtonsDisable] = useState<boolean>(false);
  const [statePlanList, setStatePlanList] = useState({
    planDataList: []
  });
  const classes = ccmCreatePlanDialogStyles();
  const [deletedIds, setDeletedIds] = useState<any>([]);

  const groupId: any = useSelector((state: any) => state.auth.groupId);
  const patientDetails = useSelector(
    (state: any) => state.patientDetailsDrawer.patientDetails
  );
  const handleClose = React.useCallback(
    (response: 'confirm' | 'cancel') => {
      if (typeof onClose === 'function') {
        onClose(response);
      }
    },
    [onClose]
  );

  const {control, handleSubmit, errors, setValue} = useForm<TaskFieldInputs>({
    mode: 'all',

    defaultValues: {
      plan: [
        {
          task_name: '',
          task_type: '',
          itemId: undefined
        }
      ]
    },
    resolver: async (values: any) => {
      let error = {};
      const {plan} = values;
      if (Array.isArray(plan) && plan.length > 0) {
        let errorFlag = false;
        const taskErrors: Array<any> = [];
        plan.forEach((task: TaskFields) => {
          let taskError = {};
          if (!task.task_name) {
            (errorFlag = true),
              (taskError = {
                ...taskError,
                task_name: {
                  message: t('CCM.CCM_TASK_NAME')
                }
              });
          }
          if (!task.task_type) {
            (errorFlag = true),
              (taskError = {
                ...taskError,
                task_type: {
                  message: t('CCM.CCM_PLAN_TASK')
                }
              });
          }
          taskErrors.push(taskError);
        });
        if (errorFlag) {
          error = {...error, plan: taskErrors};
        }
        return {values, errors: error};
      }
      return {values, errors: error};
    }
  });

  const {fields, remove, append} = useFieldArray<TaskFields>({
    name: 'plan',
    control
  });

  const fetchCCMPlanTaskDetails = async () => {
    try {
      if (ccmPatientId) {
        const options: any = {};
        if (clinicId) {
          options.clinic_id = clinicId;
        }
        if (isExternalPlanActiveProp) {
          options.is_external_care_plan = isExternalPlanActiveProp;
        }
        const response = await getPatientCCMTaskDetails(
          groupId,
          ccmPatientId,
          options
        );
        setStatePlanList(prevState => ({
          ...prevState,
          planDataList: response?.data?.rows
        }));
        // FIXME: Need to check the setValue again
        // DOC: right now the first setValue is creating the input field rows based on API response and
        // the second setValue is setting up the response data in the fields, it has be to enhanced to
        // one setValue which is the right approach
        setValue('plan', response?.data?.rows);
        response?.data?.rows?.forEach((item: any, idx: number) => {
          setValue(`plan.${idx}.task_name`, item?.task_name);
          setValue(`plan.${idx}.task_type`, item?.task_type);
          setValue(`plan.${idx}.itemId`, item?.id);
        });
      }
    } catch (err: any) {
      enqueueSnackbar(
        err.message || t('CCM.CCM_PLAN_TASK_DETAILS_FETCH_ERROR'),
        SnackBarConfig('e')
      );
    }
  };

  useEffect(() => {
    if (fromEdit === true) {
      fetchCCMPlanTaskDetails();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeletedItems = (id: number | string | undefined) => {
    // finding the id from the API response and assigning it to the deletedIds useState
    const plan = statePlanList?.planDataList?.find(
      (item: any) => item.id === id
    );
    if (id && plan && Object.keys(plan).length) {
      setDeletedIds((p: any) => [...p, id]);
    }
  };

  const renderDialogBody = () => {
    return (
      <div className={classes.dialogBody}>
        {fields?.length === 0 ? (
          <div className={classes.noPlan}>{t('CCM.CCM_PLAN_TEXT')}</div>
        ) : (
          // ? 'Atlease one plan item is required'
          fields.map((item, i) => (
            <div key={item?.id} className={classes.plansWrapper}>
              <div className={classes.taskNameWrapper}>
                <div>{t('CCM.PLAN.TASK_NAME')}*</div>
                <FormControl
                  error={!!errors?.plan?.[i]?.task_name}
                  fullWidth={true}
                  className={classes.formControl}
                  variant="outlined">
                  <Controller
                    id={`plan.${i}.task_name`}
                    as={StyledOutlinedInput}
                    margin={'dense'}
                    control={control}
                    name={`plan.${i}.task_name`}
                    type={'text'}
                    value={item?.task_name}
                    autoComplete="on"
                    placeholder={t('CCM.PLAN.ENTER_TASK_NAME')}
                    error={!!errors?.plan?.[i]?.task_name}
                  />

                  <div>
                    {errors?.plan?.[i]?.task_name && (
                      <FormHelperText>
                        {t(errors?.plan?.[i]?.task_name?.message as string)}
                      </FormHelperText>
                    )}
                  </div>
                </FormControl>
              </div>
              <div className={classes.taskTypeWrapper}>
                <div className={classes.planTaskTypeTitleWrapper}>
                  {t('CCM.PLAN.PLAN_TASK')}*
                </div>
                <FormControl
                  fullWidth={true}
                  variant="outlined"
                  className={classes.formControl}
                  error={!!errors?.plan?.[i]?.task_type}>
                  <Controller
                    id={`plan.${i}.task_type`}
                    margin={'dense'}
                    control={control}
                    name={`plan.${i}.task_type`}
                    render={({onChange, onBlur, value}): React.ReactElement => (
                      <Select
                        value={value || item?.task_type}
                        onChange={e => {
                          onChange(e);
                        }}
                        onBlur={onBlur}
                        fullWidth={true}
                        variant="outlined"
                        margin="dense"
                        IconComponent={KeyboardArrowDownIcon}
                        input={<CustomSelectField />}
                        placeholder={t('CCM.PLAN.SELECT_PLAN_TASK')}
                        className={[classes.input, classes.root].join(' ')}
                        style={
                          errors?.plan?.[i]?.task_type
                            ? stateErrorStyles
                            : item?.task_type === t('CCM.PLAN.SELECT_PLAN_TASK')
                            ? stateWithOpacity
                            : stateStyles
                        }>
                        {CCMPlanTaskArray.map(plan => (
                          <MenuItem value={plan.value} key={plan.value}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    error={!!errors?.plan?.[i]?.task_type}
                  />
                  <div>
                    {errors?.plan?.[i]?.task_type && (
                      <FormHelperText>
                        {t(errors?.plan?.[i]?.task_type?.message as string)}
                      </FormHelperText>
                    )}
                  </div>
                </FormControl>
              </div>
              <Controller
                control={control}
                name={`plan.${i}.itemId`}
                render={field => <input type="hidden" {...field} />}
              />

              <Tooltip title={`${t('Delete task')}`} placement="top">
                <IconButton
                  className={classes.deleteIconButton}
                  onClick={(): void => {
                    // ** item?.id is coming from backend through get API */
                    handleDeletedItems(item?.itemId || item?.id);
                    remove(i);
                  }}>
                  <DeleteForeverIcon className={classes.deleteIconStyle} />
                </IconButton>
              </Tooltip>
            </div>
          ))
        )}
      </div>
    );
  };
  // API's Integration for creating ccm plan and updating them //
  const onSubmitSuccess = async (values: any) => {
    const formattedValues: Record<string, any> = {};
    formattedValues.deleted_plan = deletedIds;
    formattedValues.plan = values?.plan?.map((item: any) => {
      if (item?.itemId) {
        return {
          task_name: item?.task_name,
          task_type: item?.task_type,
          id: item?.itemId
        };
      }
      return {task_name: item?.task_name, task_type: item?.task_type};
    });

    if (fromEdit) {
      try {
        const options: Record<string, any> = {};
        if (clinicId) {
          options.clinic_id = clinicId;
        }
        if (isExternalPlanActiveProp) {
          options.is_external_care_plan = isExternalPlanActiveProp;
        }
        // * update Ccm plan for patient API integration*/
        const response = await updateCCMPlan(
          groupId,
          ccmPatientId,
          formattedValues,
          options
        );
        if (response) {
          enqueueSnackbar(t('CCM.CCM_PLAN_UPDATED'), SnackBarConfig('s'));
          // ** to update the more options in the patient details drawer we are using this service ** //
          CommunicationService.sendEvent({
            action: 'CCM_PLAN_TASK_DETAILS_UPDATED',
            data: {
              ccmPatientId: patientDetails?.ccm_patient_id ?? ccmPatientId
            }
          });
          handleClose('confirm');
        }
      } catch (err: any) {
        enqueueSnackbar(
          (err && err.message) || t('CCM.API_ERROR_MESSAGES'),
          SnackBarConfig('e')
        );
        throw err;
      }
    } else {
      try {
        // * create Ccm plan for patient API integration*/
        const options: Record<string, any> = {};
        if (clinicId) {
          options.clinic_id = clinicId;
        }
        const response = await createCCMPlanforPatient(
          groupId,
          ccmPatientId,
          values,
          options
        );
        if (response) {
          enqueueSnackbar(t('CCM.CCM_PLAN_CREATED'), SnackBarConfig('s'));
          if (ccmPatientId) {
            CommunicationService.sendEvent({
              action: 'CCM_PLAN_CREATED',
              data: {
                ccmPatientId: ccmPatientId
              }
            });
          }
          handleClose('confirm');
        }
      } catch (err: any) {
        enqueueSnackbar(
          (err && err.message) || t('CCM.ERROR_IN_UPDATING_PLANS'),
          SnackBarConfig('e')
        );
        throw err;
      }
    }
  };

  return (
    <Dialog
      disableEnforceFocus
      maxWidth={'sm'}
      fullWidth={true}
      open={open}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      onClose={handleClose}
      aria-labelledby="confirm-dialog"
      aria-describedby="confirm-dialog">
      <DialogTitle disableTypography>
        {fromEdit ? (
          <div>
            {t(`PATIENT_DETAILS_DRAWER.CCM_MODULE.UPDATE_CCM_PLAN`, {
              patientName: patientName
            })}
            <strong>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</strong>
            {`${accountNumber})`}
          </div>
        ) : (
          <div>
            {t(`PATIENT_DETAILS_DRAWER.CCM_MODULE.CREATE_CCM_PLAN`, {
              patientName: patientName
            })}
            <strong>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</strong>
            {`${accountNumber})`}
          </div>
        )}
      </DialogTitle>
      <DialogContent dividers>{renderDialogBody()}</DialogContent>
      <DialogActions className={classes.dialogActions}>
        <PrimaryButton
          style={{
            height: '2.4rem',
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            fontSize: '1.06rem',
            width: '9rem',
            borderRadius: '3px'
          }}
          onClick={() => {
            append({
              task_type: '',
              task_name: '',
              itemId: ''
            });
          }}>
          {t('CCM.PLAN.ADD')}
        </PrimaryButton>

        <Button
          onClick={() => {
            setButtonsDisable(false);
            handleClose('cancel');
          }}>
          {t('COMMON.CANCEL')}
        </Button>
        <Button
          variant={'contained'}
          disabled={!fields?.length}
          type={'submit'}
          onClick={handleSubmit(onSubmitSuccess)}
          color="primary">
          {buttonsDisable ? (
            <ButtonLoader />
          ) : fromEdit ? (
            t('PATIENT_DETAILS_DRAWER.CCM_MODULE.UPDATE')
          ) : (
            t('PATIENT_DETAILS_DRAWER.CCM_MODULE.CREATE')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default WithPermission(
  CCMCreatePlanDialog,
  AppPermissions.CCM.NAME,
  AppPermissions.CCM.CREATE
);
